
// src\themes\tolica\components\ProductView.tsx

import ProductGallery from "../elements/Product/ProductGallery";
import { MainShopProduct } from "@/core/models/shop-product-model";
import ProductInformation from "../elements/Product/ProductInformation";
import ProductComments from "../elements/comment/ProductComment";
import Link from "next/link";
import ProductDetail from "../elements/Product/ProductDetail";
import RelatedProducts from "../elements/Product/RelatedProducts";
import WidgetsRender from "@/components/main/widgets/WidgetsRender";

interface GalleryData {
    image_info: Array<{
        url: string;
        alt?: string;
    }>;
}

interface ProductColor {
    product_id: string
    color_code: string
    color_code_2?: string
    color_title: string
}

interface ProductViewProps {
    product: MainShopProduct;
    gallery: GalleryData;
    colors: ProductColor[];
    id: number;
}

// Wood options – product_id as string
const DEMO_WOOD_OPTIONS = [
    { product_id: "10", color_code: "#5C3A21", color_title: "Walnut" },
    { product_id: "11", color_code: "#C4A97D", color_title: "Oak" },
    { product_id: "12", color_code: "#E8D5B7", color_title: "Beech" },
    { product_id: "13", color_code: "#3B2914", color_title: "Wenge" },
    { product_id: "14", color_code: "#8B6F4E", color_title: "Teak" },
]

// Fabric options – product_id as string
const DEMO_FABRIC_OPTIONS = [
    { product_id: "1", color_code: "#F5F0E8", color_code_2: "#4A4A48", color_title: "Ivory / Charcoal" },
    { product_id: "2", color_code: "#4A4A48", color_code_2: "#F5F0E8", color_title: "Charcoal / Ivory" },
    { product_id: "3", color_code: "#8B7355", color_code_2: "#2F4538", color_title: "Camel / Forest" },
    { product_id: "4", color_code: "#C2A78B", color_code_2: "#7B2D3F", color_title: "Sand / Burgundy" },
    { product_id: "5", color_code: "#1F3A5F", color_code_2: "#B8AFA6", color_title: "Navy / Stone" },
    { product_id: "6", color_code: "#E8DDD0", color_code_2: "#3C3C3C", color_title: "Cream / Graphite" },
    { product_id: "7", color_code: "#D4C8B8", color_code_2: "#6B5B4B", color_title: "Oat / Mocha" },
    { product_id: "8", color_code: "#F5F0E8", color_title: "Ivory Bouclé" },
    { product_id: "9", color_code: "#4A4A48", color_title: "Charcoal Linen" },
    { product_id: "15", color_code: "#8B7355", color_title: "Camel Suede" },
    { product_id: "16", color_code: "#2F4538", color_title: "Forest Velvet" },
]


export default function ProductView({ product, gallery, id }: ProductViewProps) {
    return (
        <div className="flex flex-col gap-12 px-4 lg:px-8 mb-10">
            {/* Main Product Section - Gallery + Info */}
            <div className="w-full grid grid-cols-1 lg:grid-cols-3">
                <div className="order-1 lg:order-2 col-span-1 lg:col-span-2 lg:sticky">
                    <ProductGallery images={gallery.image_info} product={product} />
                </div>

                <div className="order-2 lg:order-1 col-span-1 lg:col-span-1 mt-10 lg:mt-20 lg:sticky lg:top-24 lg:self-start">
                    <ProductInformation
                        product={product}
                        woodOptions={DEMO_WOOD_OPTIONS}
                        fabricOptions={DEMO_FABRIC_OPTIONS}
                    />
                </div>
            </div>

            {/* Product description / details */}
            <ProductDetail product={product} />

            {/* Tags */}
            {product.tags && product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center p-4" style={{
                    borderImageSlice: '4 4 4 4',
                    borderImageWidth: '4px 4px 4px 4px',
                    borderImageOutset: '.5px .5px .5px .5px',
                    borderImageRepeat: 'round round',
                    borderImageSource: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAACpF6WWAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB3SURBVHgB7dVBCoAgFEXR911JjcRVVSurXamj2on5wiCkWd9RnpkiF0FRCSEkZCKyWWsXPHjvhzy/o5JSGp1zR7V2y2una3BHtbBnuEMo0u51Ba8NFLFn3u7hF+wZNPD3KB8HKNLudUWMcYUi9nj6MxSxJy3+qBOdU0PNJhRu4gAAAABJRU5ErkJggg==)',
                    borderStyle: 'solid',
                    imageRendering: 'pixelated'
                }}>
                    {product.tags.map((tag) => (
                        <Link
                            key={tag.slug}
                            href={`/shop/tag/${tag.slug}`}
                            className="px-3 py-1.5 text-xs border border-primary-100 text-primary-700 hover:border-primary-900 hover:text-foreground transition-all duration-300 no-underline"
                        >
                            {tag.title}
                        </Link>
                    ))}
                </div>
            )}

            {/* Related products */}
            <RelatedProducts productId={id} />

            {/* Blog / Widgets */}
            {product.blog && (
                <div
                    className="w-full grid grid-cols-12 gap-x-4 gap-y-11 md:gap-x-13 md:gap-y-13 lg:gap-y-18 lg:gap-x-18 p-4"
                    style={{
                        borderImageSlice: '4 4 4 4',
                        borderImageWidth: '4px 4px 4px 4px',
                        borderImageOutset: '.5px .5px .5px .5px',
                        borderImageRepeat: 'round round',
                        borderImageSource: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAACpF6WWAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB3SURBVHgB7dVBCoAgFEXR911JjcRVVSurXamj2on5wiCkWd9RnpkiF0FRCSEkZCKyWWsXPHjvhzy/o5JSGp1zR7V2y2una3BHtbBnuEMo0u51Ba8NFLFn3u7hF+wZNPD3KB8HKNLudUWMcYUi9nj6MxSxJy3+qBOdU0PNJhRu4gAAAABJRU5ErkJggg==)',
                        borderStyle: 'solid',
                        imageRendering: 'pixelated'
                    }}
                >
                    <WidgetsRender widgets={product.blog.content} />
                </div>
            )}

            {/* Comments */}
            <ProductComments productId={id} />
        </div>
    )
}